// src/screens/DocumentsScreen.js
// ============================================================================
// DOCUMENTS SEARCH & MANAGEMENT (Optimized)
// ✅ Uses attachment-stored metadata for filtering/search
// ✅ Server-side filtering + pagination (no loading 500 parent docs anymore)
// ============================================================================

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit as limitFn,
  startAfter,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  Search,
  Filter,
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  Download,
  ExternalLink,
  Calendar,
  Package,
  Database,
  CheckSquare,
  X,
  Loader2,
  FolderOpen,
  LayoutGrid,
  List,
} from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "all", label: "All Documents", icon: FolderOpen },
  { value: "sampleRequest", label: "Sample Orders", icon: Package },
  { value: "inventory", label: "Inventory", icon: Database },
  { value: "task", label: "Tasks", icon: CheckSquare },
];

// Matches fileCategory saved on attachment docs
const FILE_TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "pdf", label: "PDFs" },
  { value: "image", label: "Images" },
  { value: "doc", label: "Documents" },
  { value: "spreadsheet", label: "Spreadsheets" },
  { value: "presentation", label: "Presentations" },
  { value: "file", label: "Other Files" },
];

const getFileIcon = (category) => {
  switch (category) {
    case "image":
      return ImageIcon;
    case "pdf":
      return FileText;
    default:
      return FileIcon;
  }
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "Unknown size";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return "Unknown date";

  let date;
  if (timestamp.toDate) date = timestamp.toDate();
  else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
  else if (typeof timestamp === "string") date = new Date(timestamp);
  else return "Unknown date";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getSourceTone = (sourceType) => {
  if (sourceType === "sampleRequest") {
    return {
      label: "Sample Order",
      icon: Package,
      classes: "text-blue-700 bg-blue-50 border-blue-200",
    };
  }
  if (sourceType === "inventory") {
    return {
      label: "Inventory",
      icon: Database,
      classes: "text-emerald-700 bg-emerald-50 border-emerald-200",
    };
  }
  return {
    label: "Task",
    icon: CheckSquare,
    classes: "text-violet-700 bg-violet-50 border-violet-200",
  };
};

const DocumentPreview = ({ doc, className = "" }) => {
  if (doc.fileCategory === "image" && doc.fileUrl) {
    return (
      <img
        src={doc.fileUrl}
        alt={doc.fileName}
        className={`h-full w-full object-cover ${className}`}
        loading="lazy"
      />
    );
  }

  if (doc.fileCategory === "pdf" && doc.fileUrl) {
    return (
      <iframe
        title={doc.fileName}
        src={`${doc.fileUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
        className={`h-full w-full border-0 pointer-events-none ${className}`}
      />
    );
  }

  const Icon = getFileIcon(doc.fileCategory);
  return (
    <div className={`flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-slate-100 via-white to-slate-50 ${className}`}>
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
        <Icon className="h-7 w-7 text-slate-500" />
      </div>
      <div className="max-w-[85%] truncate text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
        {(doc.fileCategory || "file").replace(/_/g, " ")}
      </div>
    </div>
  );
};

const DocumentPreviewTile = ({ doc, onOpen, onDownload }) => {
  const sourceTone = getSourceTone(doc.sourceType);
  const SourceIcon = sourceTone.icon;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="relative aspect-[1.02/0.78] overflow-hidden border-b border-slate-200 bg-slate-100">
        <DocumentPreview doc={doc} />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-slate-950/15 via-slate-900/5 to-transparent" />
      </div>

      <div className="space-y-2 p-2.5">
        <div className="flex items-center gap-2">
          <div className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${sourceTone.classes}`}>
            <SourceIcon className="h-3 w-3" />
            {sourceTone.label}
          </div>
          <div className="min-w-0 flex-1 text-right text-[10px] font-medium text-slate-400">
            {formatFileSize(doc.fileSize)}
          </div>
        </div>

        <div className="space-y-1">
          <div className="truncate text-[12px] font-semibold leading-4 text-slate-900">
            {doc.fileName}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <Calendar className="h-3 w-3" />
            <span>{formatDate(doc.uploadedAt)}</span>
          </div>
        </div>

        <div className="truncate text-[10px] text-slate-500">
          {doc.accountName || doc.productName || doc.taskTitle || "Open preview"}
        </div>

        <div className="flex items-center gap-1.5 pt-0.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
            className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-slate-800"
          >
            <Download className="h-3 w-3" />
            Save
          </button>
          <span className="text-[10px] font-medium text-slate-400">Preview</span>
        </div>
      </div>
    </button>
  );
};

const DocumentFinderRow = ({ doc, onOpen, onDownload }) => {
  const sourceTone = getSourceTone(doc.sourceType);
  const SourceIcon = sourceTone.icon;

  return (
    <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_130px_120px_120px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm">
      <button type="button" onClick={onOpen} className="flex min-w-0 items-center gap-3 text-left">
        <div className="h-11 w-14 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 flex-shrink-0">
          <DocumentPreview doc={doc} />
        </div>
        <div className="min-w-0">
          <div className="truncate font-semibold text-slate-900">{doc.fileName}</div>
          <div className="truncate text-xs text-slate-500">
            {doc.productName || doc.taskTitle || doc.fileCategory?.toUpperCase() || "FILE"}
          </div>
        </div>
      </button>

      <div className="min-w-0">
        <div className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${sourceTone.classes}`}>
          <SourceIcon className="h-3.5 w-3.5" />
          <span className="truncate">{sourceTone.label}</span>
        </div>
        <div className="mt-1 truncate text-xs text-slate-500">{doc.accountName || "No account"}</div>
      </div>

      <div className="truncate text-xs font-medium text-slate-600">{formatDate(doc.uploadedAt)}</div>
      <div className="truncate text-xs font-medium text-slate-600">{formatFileSize(doc.fileSize)}</div>

      <div className="flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open
        </button>
        <button
          type="button"
          onClick={onDownload}
          className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
        >
          <Download className="h-3.5 w-3.5" />
          Save
        </button>
      </div>
    </div>
  );
};

const DocumentsScreen = ({ companyId, userProfile }) => {
  const PAGE_SIZE = 200;

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [accounts, setAccounts] = useState({});
  const [users, setUsers] = useState({});

  const [hasMore, setHasMore] = useState(false);
  const [lastDocSnap, setLastDocSnap] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewMode, setViewMode] = useState("grid");

  // -----------------------------------------
  // Load accounts + users (for dropdown + display names)
  // -----------------------------------------
  useEffect(() => {
    if (!companyId) return;

    const loadStatic = async () => {
      try {
        const accountsSnap = await getDocs(collection(db, "companies", companyId, "accounts"));
        const accountsMap = {};
        accountsSnap.forEach((d) => {
          accountsMap[d.id] = { id: d.id, ...d.data() };
        });
        setAccounts(accountsMap);

        const usersSnap = await getDocs(
          query(collection(db, "users"), where("companyId", "==", companyId))
        );
        const usersMap = {};
        usersSnap.forEach((d) => {
          usersMap[d.id] = { id: d.id, ...d.data() };
        });
        setUsers(usersMap);
      } catch (err) {
        console.error("[DocumentsScreen] Error loading accounts/users:", err);
      }
    };

    loadStatic();
  }, [companyId]);

  // -----------------------------------------
  // Build query for attachments (server-side filters)
  // -----------------------------------------
  const buildAttachmentsQuery = useCallback(
    (opts = {}) => {
      const { startAfterSnap = null, pageSize = PAGE_SIZE } = opts;

      let qRef = query(
        collection(db, "attachments"),
        where("companyId", "==", companyId)
      );

      if (categoryFilter !== "all") {
        qRef = query(qRef, where("parentType", "==", categoryFilter));
      }

      if (accountFilter !== "all") {
        qRef = query(qRef, where("accountId", "==", accountFilter));
      }

      if (fileTypeFilter !== "all") {
        // expects attachment docs to have fileCategory
        qRef = query(qRef, where("fileCategory", "==", fileTypeFilter));
      }

      // Date range (createdAt)
      if (dateRangeStart) {
        const start = new Date(dateRangeStart);
        start.setHours(0, 0, 0, 0);
        qRef = query(qRef, where("createdAt", ">=", Timestamp.fromDate(start)));
      }

      if (dateRangeEnd) {
        const end = new Date(dateRangeEnd);
        end.setHours(23, 59, 59, 999);
        qRef = query(qRef, where("createdAt", "<=", Timestamp.fromDate(end)));
      }

      // Must orderBy createdAt when using date inequality
      qRef = query(qRef, orderBy("createdAt", "desc"), limitFn(pageSize));

      if (startAfterSnap) {
        qRef = query(qRef, startAfter(startAfterSnap));
      }

      return qRef;
    },
    [companyId, categoryFilter, accountFilter, fileTypeFilter, dateRangeStart, dateRangeEnd]
  );

  // -----------------------------------------
  // Load first page (reset) when filters/company change
  // -----------------------------------------
  const loadFirstPage = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setDocuments([]);
    setLastDocSnap(null);
    setHasMore(false);

    try {
      const snap = await getDocs(buildAttachmentsQuery({ pageSize: PAGE_SIZE }));

      const list = snap.docs.map((d) => {
        const data = d.data();

        const sourceType = data.parentType || "unknown";
        const parentTitle = data.parentTitle || null;

        return {
          id: d.id,
          sourceId: data.parentId || null,
          sourceType,

          fileName: data.fileName || "Unnamed file",
          fileUrl: data.downloadURL || null,
          fileSize: data.size || null,
          contentType: data.contentType || null,
          uploadedAt: data.createdAt || null,
          createdByUid: data.createdBy || null,

          // stored metadata
          accountId: data.accountId || null,
          accountName: data.accountName || null,

          parentTitle,
          // keep old field names so your UI stays compatible
          productName: sourceType === "sampleRequest" ? parentTitle : null,
          taskTitle: sourceType === "task" ? parentTitle : null,

          status: data.parentStatus || null,
          assignedToId: data.assignedToId || null,
          assignedToName: data.assignedToName || null,

          fileExtension: data.fileExtension || null,
          fileCategory: data.fileCategory || "file",
          searchText: data.searchText || "",
        };
      });

      const last = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
      setDocuments(list);
      setLastDocSnap(last);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error("[DocumentsScreen] Error loading attachments:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId, buildAttachmentsQuery]);

  // Load more
  const loadMore = useCallback(async () => {
    if (!companyId) return;
    if (!hasMore || !lastDocSnap) return;

    setLoadingMore(true);

    try {
      const snap = await getDocs(
        buildAttachmentsQuery({ startAfterSnap: lastDocSnap, pageSize: PAGE_SIZE })
      );

      const list = snap.docs.map((d) => {
        const data = d.data();

        const sourceType = data.parentType || "unknown";
        const parentTitle = data.parentTitle || null;

        return {
          id: d.id,
          sourceId: data.parentId || null,
          sourceType,

          fileName: data.fileName || "Unnamed file",
          fileUrl: data.downloadURL || null,
          fileSize: data.size || null,
          contentType: data.contentType || null,
          uploadedAt: data.createdAt || null,
          createdByUid: data.createdBy || null,

          accountId: data.accountId || null,
          accountName: data.accountName || null,

          parentTitle,
          productName: sourceType === "sampleRequest" ? parentTitle : null,
          taskTitle: sourceType === "task" ? parentTitle : null,

          status: data.parentStatus || null,
          assignedToId: data.assignedToId || null,
          assignedToName: data.assignedToName || null,

          fileExtension: data.fileExtension || null,
          fileCategory: data.fileCategory || "file",
          searchText: data.searchText || "",
        };
      });

      const last = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;

      setDocuments((prev) => [...prev, ...list]);
      setLastDocSnap(last);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error("[DocumentsScreen] Error loading more attachments:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [companyId, hasMore, lastDocSnap, buildAttachmentsQuery]);

  useEffect(() => {
    loadFirstPage();
  }, [
    loadFirstPage,
    companyId,
    categoryFilter,
    accountFilter,
    fileTypeFilter,
    dateRangeStart,
    dateRangeEnd,
  ]);

  // Client-side search across LOADED docs
  const filteredDocuments = useMemo(() => {
    let filtered = [...documents];

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((doc) => {
        const searchText = (doc.searchText || "").toLowerCase();
        const fileName = (doc.fileName || "").toLowerCase();
        const accountName = (doc.accountName || "").toLowerCase();
        const productName = (doc.productName || "").toLowerCase();
        const taskTitle = (doc.taskTitle || "").toLowerCase();

        return (
          searchText.includes(search) ||
          fileName.includes(search) ||
          accountName.includes(search) ||
          productName.includes(search) ||
          taskTitle.includes(search)
        );
      });
    }

    // These are already server-filtered, but keep for safety/UI consistency
    if (categoryFilter !== "all") {
      filtered = filtered.filter((doc) => doc.sourceType === categoryFilter);
    }

    if (accountFilter !== "all") {
      filtered = filtered.filter((doc) => doc.accountId === accountFilter);
    }

    if (fileTypeFilter !== "all") {
      filtered = filtered.filter((doc) => doc.fileCategory === fileTypeFilter);
    }

    filtered.sort((a, b) => {
      const aTime = a.uploadedAt?.seconds || 0;
      const bTime = b.uploadedAt?.seconds || 0;
      return bTime - aTime;
    });

    return filtered;
  }, [documents, searchTerm, categoryFilter, accountFilter, fileTypeFilter]);

  const getUserName = (userId) => {
    if (!userId) return "Unknown";
    const user = users[userId];
    return user?.fullName || user?.displayName || user?.email || "Unknown";
  };

  const handleDownload = (doc) => {
    if (doc.fileUrl) window.open(doc.fileUrl, "_blank");
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
    setAccountFilter("all");
    setFileTypeFilter("all");
    setDateRangeStart("");
    setDateRangeEnd("");
  };

  const hasActiveFilters =
    searchTerm.trim() !== "" ||
    categoryFilter !== "all" ||
    accountFilter !== "all" ||
    fileTypeFilter !== "all" ||
    dateRangeStart !== "" ||
    dateRangeEnd !== "";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm">Loading documents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Documents</h1>
          <p className="text-sm text-slate-600 mt-1">
            Search and manage attachments from sample orders, inventory, and tasks
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex items-center rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === "grid" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
              Preview
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <List className="h-4 w-4" />
              Finder
            </button>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${
              showFilters
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">
                Active
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by filename, account, product, task, status, assigned..."
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {CATEGORY_OPTIONS.map((cat) => {
          const Icon = cat.icon;
          const isActive = categoryFilter === cat.value;
          const count =
            cat.value === "all"
              ? documents.length
              : documents.filter((d) => d.sourceType === cat.value).length;

          return (
            <button
              key={cat.value}
              onClick={() => setCategoryFilter(cat.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${
                isActive
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Icon className="w-4 h-4" />
              {cat.label}
              <span
                className={`text-xs ${
                  isActive ? "text-blue-100" : "text-slate-500"
                }`}
              >
                ({count})
              </span>
            </button>
          );
        })}
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold text-slate-900">
              Advanced Filters
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">
                Account
              </label>
              <select
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Accounts</option>
                {Object.entries(accounts)
                  .sort(([, a], [, b]) =>
                    (a.name || "").localeCompare(b.name || "")
                  )
                  .map(([id, account]) => (
                    <option key={id} value={id}>
                      {account.name || "Unnamed Account"}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">
                File Type
              </label>
              <select
                value={fileTypeFilter}
                onChange={(e) => setFileTypeFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {FILE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">
                From Date
              </label>
              <input
                type="date"
                value={dateRangeStart}
                onChange={(e) => setDateRangeStart(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">
                To Date
              </label>
              <input
                type="date"
                value={dateRangeEnd}
                onChange={(e) => setDateRangeEnd(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      <div className="text-sm text-slate-600">
        Showing{" "}
        <span className="font-semibold text-slate-900">
          {filteredDocuments.length}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-slate-900">{documents.length}</span>{" "}
        loaded documents
        {hasMore ? <span className="text-slate-400"> (load more below)</span> : null}
      </div>

      {filteredDocuments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FolderOpen className="w-16 h-16 text-slate-300 mb-4" />
          <div className="text-lg font-medium text-slate-900 mb-2">
            No documents found
          </div>
          <div className="text-sm text-slate-600 max-w-md">
            {hasActiveFilters
              ? "Try adjusting your filters or search terms"
              : "Upload attachments to sample orders, inventory items, or tasks to see them here"}
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-4 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <>
          {viewMode === "grid" ? (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {filteredDocuments.map((doc) => (
                <DocumentPreviewTile
                  key={doc.id}
                  doc={doc}
                  onOpen={() => setSelectedDoc(doc)}
                  onDownload={() => handleDownload(doc)}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_130px_120px_120px] gap-3 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                <div>Name</div>
                <div>Location</div>
                <div>Date</div>
                <div>Size</div>
                <div className="text-right">Actions</div>
              </div>
              {filteredDocuments.map((doc) => (
                <DocumentFinderRow
                  key={doc.id}
                  doc={doc}
                  onOpen={() => setSelectedDoc(doc)}
                  onDownload={() => handleDownload(doc)}
                />
              ))}
            </div>
          )}

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading…
                  </>
                ) : (
                  "Load more"
                )}
              </button>
            </div>
          )}
        </>
      )}

      {selectedDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setSelectedDoc(null)}
          />

          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = getFileIcon(selectedDoc.fileCategory);
                  return (
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                  );
                })()}
                <div>
                  <div className="text-base font-semibold text-slate-900">
                    {selectedDoc.fileName}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatFileSize(selectedDoc.fileSize)}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedDoc(null)}
                className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                <div className="aspect-[1.45/1] w-full overflow-hidden">
                  <DocumentPreview doc={selectedDoc} />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-medium text-slate-500 mb-2">
                  Source
                </div>
                <div className="flex items-center gap-2">
                  {selectedDoc.sourceType === "sampleRequest" ? (
                    <>
                      <Package className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-600">
                        Sample Order
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckSquare className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-600">
                        Task
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {selectedDoc.accountName && (
                  <div className="rounded-xl border border-slate-200 p-3">
                    <div className="text-xs font-medium text-slate-500 mb-1">
                      Account
                    </div>
                    <div className="text-sm text-slate-900">
                      {selectedDoc.accountName}
                    </div>
                  </div>
                )}

                {selectedDoc.productName && (
                  <div className="rounded-xl border border-slate-200 p-3">
                    <div className="text-xs font-medium text-slate-500 mb-1">
                      Product
                    </div>
                    <div className="text-sm text-slate-900">
                      {selectedDoc.productName}
                    </div>
                  </div>
                )}

                {selectedDoc.taskTitle && (
                  <div className="rounded-xl border border-slate-200 p-3 col-span-2">
                    <div className="text-xs font-medium text-slate-500 mb-1">
                      Task
                    </div>
                    <div className="text-sm text-slate-900">
                      {selectedDoc.taskTitle}
                    </div>
                  </div>
                )}

                {selectedDoc.status && (
                  <div className="rounded-xl border border-slate-200 p-3">
                    <div className="text-xs font-medium text-slate-500 mb-1">
                      Status
                    </div>
                    <div className="text-sm text-slate-900 capitalize">
                      {selectedDoc.status}
                    </div>
                  </div>
                )}

                {selectedDoc.assignedToName && (
                  <div className="rounded-xl border border-slate-200 p-3">
                    <div className="text-xs font-medium text-slate-500 mb-1">
                      Assigned To
                    </div>
                    <div className="text-sm text-slate-900">
                      {selectedDoc.assignedToName}
                    </div>
                  </div>
                )}

                {selectedDoc.uploadedAt && (
                  <div className="rounded-xl border border-slate-200 p-3">
                    <div className="text-xs font-medium text-slate-500 mb-1">
                      Uploaded
                    </div>
                    <div className="text-sm text-slate-900">
                      {formatDate(selectedDoc.uploadedAt)}
                    </div>
                  </div>
                )}

                {selectedDoc.createdByUid && (
                  <div className="rounded-xl border border-slate-200 p-3 col-span-2">
                    <div className="text-xs font-medium text-slate-500 mb-1">
                      Uploaded By
                    </div>
                    <div className="text-sm text-slate-900">
                      {getUserName(selectedDoc.createdByUid)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200">
              <button
                onClick={() => setSelectedDoc(null)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium hover:bg-slate-50"
              >
                Close
              </button>
              <button
                onClick={() => handleDownload(selectedDoc)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsScreen;
